import type { Questionnaire, QuestionnaireValidation } from '@/types/questionnaire';
import { HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';

/**
 * Questionnaire validation. Pure — moved out of the Firestore query module so
 * it can be unit-tested without initialising Firebase.
 *
 * Language rule: content must exist in Arabic OR English. This app is
 * Arabic-first and an admin authoring a 36-question Arabic questionnaire must
 * be able to save it; the old rule demanded BOTH languages on every option
 * while accepting either on the question text, which rejected every real
 * questionnaire and — because the dialog swallowed the errors — looked like
 * "the questions don't save".
 */
const hasText = (t: { ar?: string; en?: string } | undefined) =>
  Boolean((t?.ar || '').trim() || (t?.en || '').trim());

export function validateQuestionnaire(questionnaire: Questionnaire, skipEmptyValidation = false): QuestionnaireValidation {
  const errors: { category: string; message: string }[] = [];
  const warnings: { category: string; message: string }[] = [];

  // Structural-only mode (new templates): just the option-count rule.
  if (skipEmptyValidation) {
    questionnaire.questions.forEach((question, index) => {
      if (question.options.length < 2) {
        errors.push({ category: question.category, message: `Question ${index + 1} must have at least 2 answer options` });
      }
      if (question.options.length > 6) {
        errors.push({ category: question.category, message: `Question ${index + 1} cannot have more than 6 answer options` });
      }
    });
    return { isValid: errors.length === 0, errors, warnings };
  }

  // An all-empty questionnaire is a valid template.
  const hasAnyContent = questionnaire.questions.some(
    (q) => hasText(q.question) || q.options.some((opt) => hasText(opt.text)),
  );
  if (!hasAnyContent) return { isValid: true, errors: [], warnings: [] };

  // A category with no real question is a WARNING, not an error: admins build
  // questionnaires gradually (Nutrition today, Fitness next week) and must be
  // able to save in between. The dialog tells them what is still missing.
  HABIT_CATEGORY_ORDER.forEach((category) => {
    const categoryQuestions = questionnaire.questions.filter((q) => q.category === category);
    const withContent = categoryQuestions.filter((q) => hasText(q.question));
    if (withContent.length === 0) {
      warnings.push({ category, message: `${category}: no question yet` });
    }
  });

  questionnaire.questions.forEach((question, index) => {
    if (question.options.length < 2) {
      errors.push({ category: question.category, message: `Question ${index + 1} must have at least 2 answer options` });
    }
    if (question.options.length > 6) {
      errors.push({ category: question.category, message: `Question ${index + 1} cannot have more than 6 answer options` });
    }

    // Options are checked only once the question itself has text.
    if (hasText(question.question)) {
      question.options.forEach((option, optionIndex) => {
        if (!hasText(option.text)) {
          errors.push({
            category: question.category,
            message: `Question ${index + 1}, Option ${optionIndex + 1} must have text in Arabic or English`,
          });
        }
        if (typeof option.score !== 'number' || option.score < 0) {
          errors.push({
            category: question.category,
            message: `Question ${index + 1}, Option ${optionIndex + 1} must have a valid score (0 or higher)`,
          });
        }
      });
    }
  });

  return { isValid: errors.length === 0, errors, warnings };
}
